import htmlEntities from 'src/assets/html_entities.json';

type RichTextMode = 'legacy' | 'structured';
type RichTextContext = 'root' | 'list' | 'li';
type SerializedNodeKind = 'inline' | 'block';

interface SerializedRichTextNode {
  html: string;
  kind: SerializedNodeKind;
  tag: string | null;
}

export class StringHelper {
  private static readonly STRUCTURED_ALLOWED_ALIGNMENTS = new Set(['left', 'center', 'right', 'justify']);
  private static readonly STRUCTURED_ALLOWED_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:', 'rtsp:']);
  private static readonly STRUCTURED_DROP_CONTENT_TAGS = new Set([
    'head',
    'meta',
    'link',
    'style',
    'script',
    'template',
    'svg',
    'math',
    'xml'
  ]);
  private static readonly STRUCTURED_PARAGRAPH_WRAPPERS = new Set([
    'article',
    'aside',
    'body',
    'center',
    'div',
    'figure',
    'figcaption',
    'footer',
    'header',
    'main',
    'nav',
    'section'
  ]);
  private static readonly STRUCTURED_INLINE_TAGS = new Set(['strong', 'em', 'u', 's', 'a', 'br']);

  /** search for all additional tags added in popular text editors like word */
  private static officeTagsRegex(): RegExp {
    return new RegExp(/<p>|<p[^>]+>|<\/p>/gi);
  }

  /** search for new lines in all formats */
  private static newLineRegex(): RegExp {
    return new RegExp(/\n|\r|\n\r/g);
  }

  /** search for HTML tags and sanitized entities like &#10; &#x022; etc. */
  private static htmlEntitiesRegex(): RegExp {
    return new RegExp(/<[^>]+>|&([a-z0-9]+|#[0-9]{1,6}|#x[0-9a-fA-F]{1,6});/giu);
  }

  private static entitiesRegex(): RegExp {
    return new RegExp(/&([a-z0-9]+|#[0-9]{1,6}|#x[0-9a-fA-F]{1,6});/giu);
  }

  private static entitiesRecord: Record<string, string> = this.initRecord('entities');
  private static emojisRecord: Record<string, string> = this.initRecord('emojis');

  private static initRecord(type: 'entities' | 'emojis'): Record<string, string> {
    const ret: Record<string, string> = {};
    htmlEntities[type].forEach((x) => {
      ret[x.entity] = x.character;
      ret[x.dec] = x.character;
    });
    return ret;
  }

  public static urlRegex(): RegExp {
    return new RegExp(
      // eslint-disable-next-line max-len, no-useless-escape
      /((?:(http|https|Http|Https|rtsp|Rtsp):\/\/(?:(?:[a-zA-Z0-9\$\-\_\.\+\!\*\'\(\)\,\;\?\&\=]|(?:\%[a-fA-F0-9]{2})){1,64}(?:\:(?:[a-zA-Z0-9\$\-\_\.\+\!\*\'\(\)\,\;\?\&\=]|(?:\%[a-fA-F0-9]{2})){1,25})?\@)?)?((?:(?:[a-zA-Z0-9][a-zA-Z0-9\-]{0,64}\.)+(?:(?:aero|app|arpa|asia|a[cdefgilmnoqrstuwxz])|(?:biz|b[abdefghijmnorstvwyz])|(?:cat|com|coop|c[acdfghiklmnoruvxyz])|d[ejkmoz]|(?:edu|e[cegrstu])|f[ijkmor]|(?:gov|g[abdefghilmnpqrstuwy])|h[kmnrtu]|(?:info|int|i[delmnoqrst])|(?:jobs|j[emop])|k[eghimnrwyz]|l[abcikrstuvy]|(?:mil|mobi|museum|m[acdghklmnopqrstuvwxyz])|(?:name|net|n[acefgilopruz])|(?:org|om)|(?:pro|p[aefghklmnrstwy])|qa|r[eouw]|s[abcdeghijklmnortuvyz]|(?:tel|travel|t[cdfghjklmnoprtvwz])|u[agkmsyz]|v[aceginu]|w[fs]|y[etu]|z[amw]))|(?:(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9])\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.(?:25[0-5]|2[0-4][0-9]|[0-1][0-9]{2}|[1-9][0-9]|[0-9])))(?:\:\d{1,5})?)(\/(?:(?:[a-zA-Z0-9\;\/\?\:\@\&\=\#\~\-\.\+\!\*\'\(\)\,\_])|(?:\%[a-fA-F0-9]{2}))*)?(?:\b|$)/gi
    );
  }

  public static parseUrlLink(text: string): string {
    return text.replace(this.urlRegex(), (url) => {
      const noHttp = !url.startsWith('http');
      return `<a href="${noHttp ? 'http://' + url : url}" target="_blank">${url}</a>`;
    });
  }

  // replaces each int inside string with single char
  public static numString(s: string): string {
    const ch0 = +'0';
    const ch9 = +'9';
    let ret = '';
    let last = 0;
    for (let i = 0; i < s.length; i++) {
      const char = +s[i];
      if (char >= ch0 && char <= ch9) {
        last *= 10;
        last += char - ch0;
      } else {
        if (last) {
          ret += String.fromCharCode(last);
          last = 0;
        }
      }
      ret += s[i];
    }
    if (last) ret += String.fromCharCode(last);
    return ret;
  }

  /**
   * Replace all html entities (or hashCodes) with characters from map
   * @param {string} htmlString
   * @return {string}
   */
  public static processValueToEntity(htmlString: string): string {
    return htmlString.replace(this.entitiesRegex(), (match) => this.decodeEntity(match));
  }

  private static decodeEntity(match: string): string {
    const fromRecord = this.entitiesRecord[match] || this.emojisRecord[match];
    if (fromRecord) return fromRecord;

    const dec = match.match(/^&#([0-9]{1,7});$/i);
    if (dec) {
      const cp = Number(dec[1]);
      if (Number.isInteger(cp) && cp >= 0 && cp <= 0x10ffff) return String.fromCodePoint(cp);
      return '';
    }

    const hex = match.match(/^&#x([0-9a-f]{1,6});$/i);
    if (hex) {
      const cp = Number.parseInt(hex[1], 16);
      if (Number.isInteger(cp) && cp >= 0 && cp <= 0x10ffff) return String.fromCodePoint(cp);
      return '';
    }

    return match;
  }

  public static processHtmlString(htmlString: string): string {
    if (this.htmlEntitiesRegex().test(htmlString)) {
      return htmlString;
    } else {
      return this.parseUrlLink(htmlString);
    }
  }

  public static removeAdditionalTags(htmlString: string): string {
    return htmlString.replace(this.officeTagsRegex(), '');
  }

  public static replaceNewLines(htmlString: string): string {
    return htmlString.replace(this.newLineRegex(), '<br/>');
  }

  public static prepareClipboardHtml(htmlString: string): string {
    if (!htmlString?.length) return '';

    if (!this.containsHtmlTags(htmlString)) {
      return this.prepareClipboardText(this.processValueToEntity(htmlString));
    }

    const doc = this.parseHtml(htmlString);
    return this.serializeNodes(doc.body.childNodes, 'root');
  }

  public static prepareClipboardText(text: string): string {
    const lines = (text ?? '').replace(/\r\n?/g, '\n').split('\n');
    const ret: string[] = [];
    let paragraph: string[] = [];

    const flushParagraph = () => {
      if (!paragraph.length) return;
      ret.push(`<p>${paragraph.join('<br/>')}</p>`);
      paragraph = [];
    };

    for (const line of lines) {
      if (!line.length) {
        flushParagraph();
        ret.push('<p><br/></p>');
      } else {
        paragraph.push(this.escapeHtml(line));
      }
    }

    flushParagraph();

    while (ret.length && ret[0] === '<p><br/></p>') {
      ret.shift();
    }
    while (ret.length && ret[ret.length - 1] === '<p><br/></p>') {
      ret.pop();
    }

    return ret.join('');
  }

  public static getRichTextVisibleText(htmlString: string, mode: RichTextMode = 'legacy'): string {
    if (mode !== 'structured') {
      return this.processValueToEntity(htmlString ?? '');
    }

    if (!htmlString?.length) return '';

    const doc = this.parseHtml(htmlString);
    const parts: string[] = [];
    this.appendVisibleText(doc.body.childNodes, parts, true);
    return parts
      .join('')
      .replace(/\u00a0/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  public static hasVisibleRichText(htmlString: string, mode: RichTextMode = 'legacy'): boolean {
    return this.getRichTextVisibleText(htmlString, mode).replace(/[\s\u00a0]/g, '').length > 0;
  }

  public static richTextParseUrlLink(text: string, searchDepth: number = 1): string {
    let parsedText = text.substring(0);
    let execText: RegExpExecArray | null = null;
    const regExInstance = this.urlRegex();
    while ((execText = regExInstance.exec(text)) !== null) {
      const execStart = text.substring(execText.index);
      if (text.substring(execText.index - 6, execText.index) === 'href="') continue;
      const htmlTagRegex = /<[^>]+>/g;
      function addTags() {
        const addedTagLink = StringHelper.parseUrlLink(execText![0]);
        const temp = execStart.replace(execText![0], addedTagLink);
        parsedText = parsedText.replace(execStart, temp);
      }

      let tagFound = false;
      for (let i = 0; i < searchDepth; ++i) {
        const tagStart = htmlTagRegex.exec(execStart);
        if (!tagStart) {
          break;
        } else {
          // ? break loop if it's not closing tag
          if (!tagStart[0].includes('/')) break;

          if (tagStart[0] === '</a>') {
            tagFound = true;
            break;
          }
        }
      }
      if (!tagFound) addTags();
    }
    return parsedText;
  }

  /**
   * Removes all non-digit characters
   * @param str string
   * @return string containing only digits
   */
  public static getDigits(str: string): string {
    return str.replace(/\D/g, '');
  }

  private static containsHtmlTags(htmlString: string): boolean {
    return /<\/?[a-z][^>]*>/i.test(htmlString);
  }

  private static parseHtml(htmlString: string): Document {
    return new DOMParser().parseFromString(htmlString, 'text/html');
  }

  private static serializeNodes(nodes: ArrayLike<ChildNode>, context: RichTextContext): string {
    const ret: string[] = [];
    let inlineBuffer = '';

    const flushInlineBuffer = () => {
      const preparedInline = this.trimInlineBuffer(inlineBuffer);
      inlineBuffer = '';
      if (!preparedInline) return;

      switch (context) {
        case 'root':
          ret.push(`<p>${preparedInline}</p>`);
          break;
        case 'list':
          ret.push(`<li>${preparedInline}</li>`);
          break;
        case 'li':
          ret.push(preparedInline);
          break;
      }
    };

    for (const node of Array.from(nodes)) {
      const serialized = this.serializeNode(node, context);
      if (!serialized.html) continue;

      if (serialized.kind === 'block') {
        flushInlineBuffer();
        if (context === 'list' && serialized.tag !== 'li') {
          ret.push(`<li>${serialized.html}</li>`);
        } else {
          ret.push(serialized.html);
        }
      } else {
        inlineBuffer += serialized.html;
      }
    }

    flushInlineBuffer();
    return ret.join('');
  }

  private static serializeNode(node: ChildNode, context: RichTextContext): SerializedRichTextNode {
    if (node.nodeType === Node.TEXT_NODE) {
      return { html: this.escapeHtml((node.textContent ?? '').replace(/\u00a0/g, ' ')), kind: 'inline', tag: null };
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return { html: '', kind: 'inline', tag: null };
    }

    const element = node as HTMLElement;
    const tag = this.mapTagName(element.tagName);
    if (!tag) return { html: '', kind: 'inline', tag: null };
    if (this.STRUCTURED_DROP_CONTENT_TAGS.has(tag)) return { html: '', kind: 'inline', tag: null };

    if (tag === 'br') {
      return { html: '<br/>', kind: 'inline', tag };
    }

    if (tag === 'ul' || tag === 'ol') {
      const align = this.getAllowedAlignment(element);
      const content = this.serializeNodes(element.childNodes, 'list');
      return content.length
        ? { html: `<${tag}${align}>${content}</${tag}>`, kind: 'block', tag }
        : { html: '', kind: 'block', tag };
    }

    if (tag === 'li') {
      const align = this.getAllowedAlignment(element);
      const content = this.serializeNodes(element.childNodes, 'li');
      const html = content.length ? `<li${align}>${content}</li>` : `<li${align}><br/></li>`;
      return { html, kind: 'block', tag };
    }

    if (tag === 'blockquote') {
      const align = this.getAllowedAlignment(element);
      const content = this.serializeNodes(element.childNodes, 'root');
      return content.length
        ? { html: `<blockquote${align}>${content}</blockquote>`, kind: 'block', tag }
        : { html: '', kind: 'block', tag };
    }

    if (tag === 'p' || /^h[1-6]$/.test(tag)) {
      const content = this.serializeInlineChildren(element.childNodes);
      const align = this.getAllowedAlignment(element);
      const html = content.length ? `<${tag}${align}>${content}</${tag}>` : `<${tag}${align}><br/></${tag}>`;
      return { html, kind: 'block', tag };
    }

    if (this.STRUCTURED_PARAGRAPH_WRAPPERS.has(tag)) {
      return { html: this.serializeParagraphWrapper(element, context), kind: 'block', tag };
    }

    if (this.STRUCTURED_INLINE_TAGS.has(tag)) {
      if (tag === 'a') {
        const content = this.serializeInlineChildren(element.childNodes);
        if (!content.length) return { html: '', kind: 'inline', tag };

        return { html: this.serializeAnchor(content, element), kind: 'inline', tag };
      }

      const content = this.serializeInlineChildren(element.childNodes);
      if (!content.length) return { html: '', kind: 'inline', tag };
      return { html: `<${tag}>${content}</${tag}>`, kind: 'inline', tag };
    }

    return { html: this.serializeInlineChildren(element.childNodes), kind: 'inline', tag: null };
  }

  private static serializeInlineChildren(nodes: ArrayLike<ChildNode>): string {
    return Array.from(nodes)
      .map((node) => this.serializeInlineNode(node))
      .join('');
  }

  private static serializeParagraphWrapper(element: HTMLElement, context: RichTextContext): string {
    const ret: string[] = [];
    const align = this.getAllowedAlignment(element);
    let inlineBuffer = '';

    const pushInContext = (html: string, tag: string | null = null) => {
      if (context === 'list' && tag !== 'li') {
        ret.push(`<li>${html}</li>`);
        return;
      }

      ret.push(html);
    };

    const flushInlineBuffer = () => {
      const preparedInline = this.trimInlineBuffer(inlineBuffer);
      inlineBuffer = '';
      if (!preparedInline) return;

      const paragraph = `<p${align}>${preparedInline}</p>`;
      pushInContext(paragraph);
    };

    for (const child of Array.from(element.childNodes)) {
      const serialized = this.serializeNode(child, context);
      if (!serialized.html) continue;

      if (serialized.kind === 'block') {
        flushInlineBuffer();
        const blockHtml = this.applyAlignmentToBlockHtml(serialized.html, align);
        pushInContext(blockHtml, serialized.tag);
      } else {
        inlineBuffer += serialized.html;
      }
    }

    flushInlineBuffer();
    return ret.join('');
  }

  private static serializeInlineNode(node: ChildNode): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return this.escapeHtml((node.textContent ?? '').replace(/\u00a0/g, ' '));
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const element = node as HTMLElement;
    const tag = this.mapTagName(element.tagName);
    if (!tag || this.STRUCTURED_DROP_CONTENT_TAGS.has(tag)) return '';

    if (tag === 'br') return '<br/>';

    if (tag === 'a') {
      const content = this.serializeInlineChildren(element.childNodes);
      if (!content.length) return '';

      return this.serializeAnchor(content, element);
    }

    if (this.STRUCTURED_INLINE_TAGS.has(tag)) {
      const content = this.serializeInlineChildren(element.childNodes);
      return content.length ? `<${tag}>${content}</${tag}>` : '';
    }

    if (tag === 'p' || /^h[1-6]$/.test(tag) || tag === 'blockquote' || tag === 'ul' || tag === 'ol' || tag === 'li') {
      return this.visibleTextToInlineHtml(this.serializeNodes([node], 'root'));
    }

    return this.serializeInlineChildren(element.childNodes);
  }

  private static visibleTextToInlineHtml(htmlString: string): string {
    const text = this.getRichTextVisibleText(htmlString, 'structured');
    return text.length ? this.escapeHtml(text).replace(this.newLineRegex(), '<br/>') : '';
  }

  private static trimInlineBuffer(htmlString: string): string {
    const trimmed = htmlString.replace(/^(?:\s|<br\s*\/?>)+/gi, '').replace(/(?:\s|<br\s*\/?>)+$/gi, '');

    return this.hasVisibleRichText(trimmed, 'structured') ? trimmed : '';
  }

  private static mapTagName(tagName: string): string | null {
    const tag = tagName.toLowerCase();
    if (tag.includes(':')) return null;

    switch (tag) {
      case 'b':
        return 'strong';
      case 'i':
        return 'em';
      case 'strike':
        return 's';
      default:
        return tag;
    }
  }

  private static getAllowedAlignment(element: HTMLElement): string {
    const align = (element.getAttribute('align') ?? element.style.textAlign ?? '').trim().toLowerCase();
    return this.STRUCTURED_ALLOWED_ALIGNMENTS.has(align) ? ` align="${align}"` : '';
  }

  private static applyAlignmentToBlockHtml(htmlString: string, align: string): string {
    if (!align.length || !htmlString.length || htmlString.includes(' align=')) return htmlString;

    return htmlString.replace(/^<([a-z0-9]+)/i, `<$1${align}`);
  }

  private static serializeAnchor(content: string, element: HTMLElement): string {
    const href = this.getSafeStructuredHref(element.getAttribute('href'));
    if (!href.length) return content;

    const target = element.getAttribute('target') === '_blank' ? ' target="_blank"' : '';
    return `<a href="${this.escapeHtml(href)}"${target}>${content}</a>`;
  }

  private static getSafeStructuredHref(rawHref: string | null): string {
    const href = (rawHref ?? '').trim();
    if (!href.length) return '';

    if (href.startsWith('//')) return '';

    if (/^(?:#|\/|\?|\.\.?\/)/.test(href)) {
      return href;
    }

    const protocolMatch = href.match(/^([a-z][a-z0-9+.-]*:)/i);
    if (!protocolMatch) return '';

    return this.STRUCTURED_ALLOWED_URL_PROTOCOLS.has(protocolMatch[1].toLowerCase()) ? href : '';
  }

  private static escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  private static appendVisibleText(nodes: ArrayLike<ChildNode>, parts: string[], isRoot: boolean = false): void {
    for (const node of Array.from(nodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        parts.push((node.textContent ?? '').replace(/\u00a0/g, ' '));
        continue;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        continue;
      }

      const element = node as HTMLElement;
      const tag = this.mapTagName(element.tagName);
      if (!tag || this.STRUCTURED_DROP_CONTENT_TAGS.has(tag)) continue;

      if (tag === 'br') {
        parts.push('\n');
        continue;
      }

      const isBlock =
        isRoot || tag === 'p' || tag === 'blockquote' || tag === 'li' || tag === 'ul' || tag === 'ol' || /^h[1-6]$/.test(tag);
      const startLength = parts.length;
      if (isBlock && parts.length && !parts[parts.length - 1].endsWith('\n')) {
        parts.push('\n');
      }

      this.appendVisibleText(element.childNodes, parts);

      if (isBlock && parts.length && !parts[parts.length - 1].endsWith('\n')) {
        parts.push('\n');
      }

      if (startLength === parts.length && tag === 'li') {
        parts.push('\n');
      }
    }
  }
}
